/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-01-11 12:59:33
 **/

import { Children, ReactElement } from "react";

/**
 * Represents properties for rendering a collection of items.
 *
 * @template T - The type of items to be rendered.
 * @property {function} render - A function that takes an item and its index as input and returns a ReactElement or null.
 * @property {T[] | undefined} of - The array of items to be iterated over and rendered.
 * @property {boolean} [useArray] - Optional. Indicates if the array should be utilized in specific rendering behavior.
 * @property {boolean} [hidden] - Optional. Determines whether the rendering should be hidden or skipped.
 */
type EachProps<T> = {
	render: (item: T, index: number) => ReactElement | null;
	of: T[] | undefined;
	useArray?: boolean;
	hidden?: boolean;
};

/**
 * Renders a collection of items using the provided render function, with optional flags for hiding the result or using an array of elements.
 *
 * @param {Object} EachProps - Configuration object for rendering.
 * @param {function} EachProps.render - A callback function that renders each individual item. Receives the current item and its index as arguments.
 * @param {Array<T>} EachProps.of - The collection of items to render. If null or undefined, the method returns null.
 * @param {boolean} [EachProps.hidden=false] - Flag to determine whether the output should be hidden. If true, the method returns null.
 * @param {boolean} [EachProps.useArray=false] - Flag indicating whether to return the result as an array using `Children.toArray`.
 * @return {Array|null} The rendered result as an array or null if `hidden` is true or `of` is not provided.
 */
export default function Each<T>({ render, of, hidden = false, useArray = false }: EachProps<T>) {
	if (hidden || !of) {
		return null;
	}

	const child_render_list = of.map((item, index) => render(item, index)).filter(Boolean);
	return useArray ? Children.toArray(child_render_list) : child_render_list;
}
